import React, { useState } from "react";
import { motion } from "framer-motion";
import logo from "../assets/logo.png";
import {
  CreditCard,
  Calendar,
  Download,
  Shield,
  Gift,
  Crown,
  Star,
  CheckCircle,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SubscriptionCard = ({ subscription, index }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const formatAmount = (amount) => {
    if (amount === 0) return "Free";
    return `₹${(amount / 100).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Map plans to light mode styles
  const getPlanDetails = (planType, amount) => {
    const base = {
      // card container colors
      bgCard: "bg-white",
      border: "border-gray-200",
      // gradient icon pill
      iconPill: "from-[#01CC54] to-[#01CC54]/90",
      // plan title color
      title: "text-gray-900",
      // amount color
      amount: "text-[#01CC54]",
      // muted text
      muted: "text-gray-600",
    };
    switch (planType) {
      case "free_trial":
        return {
          ...base,
          name: "Free Trial",
          icon: Gift,
          iconPill: "from-[#01CC54] to-[#01CC54]/90",
          badge: "text-[#01CC54]",
        };
      case "1_month":
        return {
          ...base,
          name: "1 Month Plan",
          icon: Calendar,
          iconPill: "from-blue-500 to-blue-600",
          badge: "text-blue-600",
        };
      case "6_months":
        return {
          ...base,
          name: "6 Months Plan",
          icon: Star,
          iconPill: "from-violet-500 to-violet-600",
          badge: "text-violet-600",
        };
      case "12_months":
        return {
          ...base,
          name: "12 Months Plan",
          icon: Crown,
          iconPill: "from-amber-500 to-amber-600",
          badge: "text-amber-600",
        };
      default:
        return {
          ...base,
          name: "Unknown Plan",
          icon: Shield,
          iconPill: "from-gray-500 to-gray-600",
          badge: "text-gray-600",
        };
    }
  };

  const isExpired = new Date(subscription.expires_at) < new Date();
  const daysLeft = Math.ceil(
    (new Date(subscription.expires_at) - new Date()) / (1000 * 60 * 60 * 24)
  );
  const planDetails = getPlanDetails(
    subscription.plan_type,
    subscription.amount
  );
  const IconComponent = planDetails.icon;

  const downloadInvoice = async () => {
    if (subscription.plan_type === "free_trial") {
      toast.info("No invoice available for free trial");
      return;
    }
    setIsDownloading(true);
    try {
      await generatePDFInvoice(subscription);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper to avoid missing ₹ in PDFs by replacing with Rs.
  const addTextWithRupee = (pdf, text, x, y) => {
    if (text.includes("₹")) {
      const modifiedText = text.replace(/₹/g, "Rs. ");
      pdf.text(modifiedText, x, y);
    } else {
      pdf.text(text, x, y);
    }
  };

  const generatePDFInvoice = async (sub) => {
    const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const primaryColor = [26, 26, 46];
    const accentColor = [255, 107, 53];
    const successColor = [39, 174, 96];
    const lightBlue = [248, 249, 255];
    const textDark = [44, 62, 80];
    const textLight = [127, 140, 141];
    const borderColor = [225, 232, 237];

    const calculateGST = (amount) => {
      const totalAmount = amount;
      const baseAmount = Math.round(totalAmount / 1.18);
      const gstAmount = totalAmount - baseAmount;
      return { baseAmount, gstAmount, totalAmount };
    };

    const gstCalc = calculateGST(sub.amount);
    const planInfo = {
      name: sub.plan_type.replace("_", " ").toUpperCase(),
      duration: 1,
      durationType: "month",
    };

    const invoiceNumber = `INV-${
      sub.id?.toString().padStart(6, "0") || sub.razorpay_order_id
    }`;
    const invoiceDate = formatDate(sub.created_at || new Date());
    const startDate = formatDate(new Date());
    const endDate = formatDate(sub.expires_at);

    // Header
    pdf.setFillColor(...primaryColor);
    pdf.rect(10, 10, 190, 35, "F");
    pdf.setFillColor(...accentColor);
    pdf.rect(10, 42, 190, 3, "F");

    try {
      const logoData = await processImageForPDF(logo, 200, 200, 0.8);
      const logoSize = 18;
      const logoCenterX = 27;
      const logoCenterY = 27.5;
      const circleRadius = logoSize / 2 + 3;

      pdf.setFillColor(255, 255, 255);
      pdf.circle(logoCenterX, logoCenterY, circleRadius, "F");
      pdf.addImage(
        logoData.dataURL,
        "PNG",
        logoCenterX - logoSize / 2,
        logoCenterY - logoSize / 2,
        logoSize,
        logoSize
      );
    } catch (error) {
      const fallbackSize = 18;
      const fallbackX = 27;
      const fallbackY = 27.5;
      pdf.setFillColor(255, 255, 255);
      pdf.circle(fallbackX, fallbackY, fallbackSize / 2 + 3, "F");
      pdf.setFillColor(...accentColor);
      pdf.circle(fallbackX, fallbackY, fallbackSize / 2, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("💬", fallbackX - 3, fallbackY + 2);
    }

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("Ping By BluTec.ai", 50, 25);

    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text("WhatsApp Business API Platform", 50, 32);
    pdf.text("sales@blutec.ai | https://ping.blutec.ai", 50, 38);

    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.text("INVOICE", 155, 22);

    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.text(invoiceNumber, 155, 30);
    pdf.text(invoiceDate, 155, 36);

    // Status badge
    pdf.setFillColor(...successColor);
    pdf.roundedRect(160, 50, 30, 8, 3, 3, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.text("PAID", 170, 56);

    // Cards
    let currentY = 65;
    const cardWidth = 85;
    const cardHeight = 35;

    // Billing card
    pdf.setFillColor(...lightBlue);
    pdf.setDrawColor(...borderColor);
    pdf.roundedRect(15, currentY, cardWidth, cardHeight, 3, 3, "FD");

    pdf.setTextColor(...accentColor);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("BILLING DETAILS", 20, currentY + 8);

    pdf.setTextColor(...textDark);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.text(user.name || "Valued Customer", 20, currentY + 15);

    pdf.setFont("helvetica", "normal");
    const emailText =
      user.email?.length > 25
        ? user.email.substring(0, 22) + "..."
        : user.email || "";
    pdf.text(emailText, 20, currentY + 20);
    pdf.text(
      `Instance: ${(sub.instance_name || "Default").substring(0, 18)}`,
      20,
      currentY + 25
    );
    pdf.text(`ID: ${sub.id || "N/A"}`, 20, currentY + 30);

    // Subscription card
    pdf.setFillColor(...lightBlue);
    pdf.roundedRect(110, currentY, cardWidth, cardHeight, 3, 3, "FD");

    pdf.setTextColor(...accentColor);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("SUBSCRIPTION", 115, currentY + 8);

    pdf.setTextColor(...textDark);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.text(planInfo.name, 115, currentY + 15);

    pdf.setFont("helvetica", "normal");
    pdf.text(
      `Duration: ${planInfo.duration} ${planInfo.durationType}(s)`,
      115,
      currentY + 20
    );
    pdf.text(`Valid: ${startDate} to ${endDate}`, 115, currentY + 25);
    pdf.text("Status: Active", 115, currentY + 30);

    currentY += cardHeight + 15;

    // Table
    const tableWidth = 180;
    const tableHeaderHeight = 12;

    pdf.setFillColor(...primaryColor);
    pdf.roundedRect(15, currentY, tableWidth, tableHeaderHeight, 2, 2, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.text("DESCRIPTION", 20, currentY + 8);
    pdf.text("PERIOD", 90, currentY + 8);
    pdf.text("AMOUNT", 125, currentY + 8);
    pdf.text("GST (18%)", 160, currentY + 8);

    currentY += tableHeaderHeight;

    const rowHeight = 15;
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(...borderColor);
    pdf.rect(15, currentY, tableWidth, rowHeight, "FD");

    pdf.setTextColor(...textDark);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.text(planInfo.name, 20, currentY + 8);

    pdf.setTextColor(...textLight);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      `${planInfo.duration} ${planInfo.durationType}(s)`,
      90,
      currentY + 8
    );
    pdf.text(
      `Rs ${(gstCalc.baseAmount / 100).toLocaleString("en-IN")}`,
      125,
      currentY + 8
    );
    pdf.text(
      `Rs ${(gstCalc.gstAmount / 100).toLocaleString("en-IN")}`,
      160,
      currentY + 8
    );

    currentY += rowHeight + 10;

    // Totals
    const totalsWidth = 70;
    const totalsHeight = 35;
    const totalsX = 125;

    pdf.setFillColor(...lightBlue);
    pdf.setDrawColor(...borderColor);
    pdf.roundedRect(totalsX, currentY, totalsWidth, totalsHeight, 3, 3, "FD");

    pdf.setTextColor(...textDark);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text("Subtotal", totalsX + 5, currentY + 8);
    pdf.text(
      `Rs ${(gstCalc.baseAmount / 100).toLocaleString("en-IN")}`,
      totalsX + 40,
      currentY + 8
    );

    pdf.text("GST (18%)", totalsX + 5, currentY + 15);
    pdf.text(
      `Rs ${(gstCalc.gstAmount / 100).toLocaleString("en-IN")}`,
      totalsX + 40,
      currentY + 15
    );

    pdf.setDrawColor(...borderColor);
    pdf.line(totalsX + 5, currentY + 18, totalsX + 65, currentY + 18);

    pdf.setTextColor(...accentColor);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("TOTAL", totalsX + 5, currentY + 28);
    addTextWithRupee(
      pdf,
      `Rs ${(gstCalc.totalAmount / 100).toLocaleString("en-IN")}`,
      totalsX + 40,
      currentY + 28
    );

    currentY += totalsHeight + 15;

    // Payment info
    pdf.setTextColor(...accentColor);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "bold");
    pdf.text("PAYMENT INFORMATION", 15, currentY);

    currentY += 8;
    const paymentBoxHeight = 20;
    pdf.setFillColor(248, 249, 250);
    pdf.setDrawColor(...borderColor);
    pdf.roundedRect(15, currentY, 180, paymentBoxHeight, 2, 2, "FD");

    pdf.setTextColor(...textDark);
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      `Payment ID: ${sub.razorpay_payment_id || "N/A"}`,
      20,
      currentY + 6
    );
    pdf.text(`Order ID: ${sub.razorpay_order_id || "N/A"}`, 20, currentY + 12);
    pdf.text(
      `Method: Online Payment | Status: SUCCESS | Date: ${invoiceDate}`,
      20,
      currentY + 18
    );

    currentY += paymentBoxHeight + 10;

    // Terms
    pdf.setTextColor(...textLight);
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "normal");
    const termsText =
      "Terms & Conditions: This invoice is valid for the subscription period mentioned above. For support or queries, contact sales@blutec.ai. All payments are processed securely through Razorpay.";
    const splitText = pdf.splitTextToSize(termsText, 180);
    pdf.text(splitText, 15, currentY);
    currentY += splitText.length * 4 + 10;

    // Footer
    const footerHeight = 20;
    const footerY = 277;
    pdf.setFillColor(...primaryColor);
    pdf.rect(0, footerY, 210, footerHeight, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("Thank you for choosing Ping!", 15, footerY + 8);

    pdf.setFontSize(6);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      "For support: sales@blutec.ai | Visit: https://ping.blutec.ai",
      15,
      footerY + 14
    );
    pdf.text(
      `Generated on ${new Date().toLocaleString(
        "en-IN"
      )} | Computer Generated Invoice`,
      120,
      footerY + 16
    );

    pdf.setFillColor(...accentColor);
    pdf.rect(0, 297, 210, 2, "F");

    pdf.save(`invoice-${sub.razorpay_order_id || invoiceNumber}.pdf`);
  };

  // Image processing helper
  const processImageForPDF = async (
    imageUrl,
    maxWidth = 200,
    maxHeight = 200,
    quality = 0.8
  ) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let { width, height } = img;
        const aspectRatio = width / height;
        if (width > height) {
          width = Math.min(width, maxWidth);
          height = width / aspectRatio;
        } else {
          height = Math.min(height, maxHeight);
          width = height * aspectRatio;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);
        const dataURL = canvas.toDataURL("image/png", quality);
        resolve({ dataURL, width, height });
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = imageUrl;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`${planDetails.bgCard} ${planDetails.border} border-2 rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br ${planDetails.iconPill} rounded-full flex items-center justify-center shadow-lg`}
          >
            <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </motion.div>
          <div>
            <h3 className={`font-bold text-lg md:text-xl ${planDetails.title}`}>
              {planDetails.name}
            </h3>
            <p className={`${planDetails.muted} text-sm`}>
              {subscription.instance_name}
            </p>
          </div>
        </div>

        <div className="text-right">
          <div
            className={`text-2xl md:text-3xl font-bold ${planDetails.amount}`}
          >
            {formatAmount(subscription.amount)}
          </div>
          {subscription.plan_type !== "free_trial" && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={downloadInvoice}
              disabled={isDownloading}
              className="mt-2 flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-200 shadow transition text-sm font-medium"
            >
              {isDownloading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-[#01CC54]/40 border-t-[#01CC54] rounded-full"
                />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Invoice
            </motion.button>
          )}
        </div>
      </div>

      {/* Status and Dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Started</p>
            <p className="font-semibold text-sm text-gray-900">
              {formatDate(subscription.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <div>
            <p className="text-xs text-gray-500">Expires</p>
            <p className="font-semibold text-sm text-gray-900">
              {formatDate(subscription.expires_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {subscription.subscription_status === "active" && !isExpired ? (
              <CheckCircle className="w-5 h-5 text-[#01CC54]" />
            ) : (
              <Clock className="w-5 h-5 text-red-600" />
            )}
          </motion.div>
          <span
            className={`font-semibold text-sm ${
              subscription.subscription_status === "active" && !isExpired
                ? "text-[#01CC54]"
                : "text-red-600"
            }`}
          >
            {subscription.subscription_status === "active" && !isExpired
              ? "Active"
              : "Expired"}
          </span>
        </div>

        {subscription.subscription_status === "active" && !isExpired && (
          <div className="text-right">
            <p className="text-xs text-gray-600">
              {daysLeft > 0 ? `${daysLeft} days left` : "Expires today"}
            </p>
          </div>
        )}
      </div>

      {/* Payment Details for paid plans */}
      {subscription.plan_type !== "free_trial" && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-4 pt-4 border-t border-gray-200"
        >
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <CreditCard className="w-4 h-4" />
            <span>Payment ID: {subscription.razorpay_payment_id}</span>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SubscriptionCard;
